/* ============================================================================
 * HOME_OFFICE.EXE — the office run (Enzyme)
 * ----------------------------------------------------------------------------
 * Ported from the design source (SupercomputeA/supercompute-immersion →
 * app-home-office.html): the runsheet, payables, supplies and office switches.
 *
 * WHAT IS REAL: the runsheet ticks and the switches are held in this browser.
 * WHAT IS NOT, AND SAYS SO: payables and supplies are a specimen sheet —
 * sample rows, no rail, no charge. Nothing here moves money.
 * ========================================================================= */

import { useEffect, useState } from 'react';
import { SkinArt } from '../components/SkinArt';

interface Task {
  id: string;
  title: string;
  owner: string;
  due: string;
  overdue?: boolean;
}

const SEED_TASKS: readonly Task[] = [
  { id: 't1', title: 'Reconcile Hostinger invoices', owner: 'finance', due: '28 SEP', overdue: true },
  { id: 't2', title: 'Top up the AgentCard float', owner: 'finance', due: '03 OCT' },
  { id: 't3', title: 'Verify the Proton bridge on this machine', owner: 'desk', due: '05 OCT' },
  { id: 't4', title: 'Publish the weekly dispatch', owner: 'publishing', due: '07 OCT' },
  { id: 't5', title: 'Check the queue against node load', owner: 'desk', due: '09 OCT' },
];

const OWNERS = ['operator', 'desk', 'finance', 'publishing'] as const;

const DONE_KEY = 'sc.office.done';
const EXTRA_KEY = 'sc.office.extra';

function readDone(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DONE_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}
function readExtra(): Task[] {
  try {
    return JSON.parse(localStorage.getItem(EXTRA_KEY) ?? '[]') as Task[];
  } catch {
    return [];
  }
}

const TABS = [
  { id: 'runsheet', label: 'Runsheet' },
  { id: 'payables', label: 'Payables' },
  { id: 'supplies', label: 'Supplies' },
  { id: 'switches', label: 'Switches' },
] as const;
type Tab = (typeof TABS)[number]['id'];

const PAYABLES = [
  { vendor: 'Hostinger', charge: 'Business hosting', sub: 'Subscription 1', amount: '$24.49' },
  { vendor: 'Hostinger', charge: 'Business hosting', sub: 'Subscription 2', amount: '$21.99' },
];

const SUPPLIES = [
  { item: 'Mailers', sub: 'Rigid, navy print', onHand: 180, reorder: 60, low: false },
  { item: 'Shipping labels', sub: '', onHand: 240, reorder: 80, low: false },
  { item: 'Packing tape', sub: '', onHand: 4, reorder: 6, low: true },
  { item: 'Inserts', sub: 'Brass card, one per parcel', onHand: 96, reorder: 40, low: false },
];

interface Switches {
  autopay: boolean;
  receipts: boolean;
  digest: boolean;
}

function readSwitches(): Switches {
  try {
    const v = JSON.parse(localStorage.getItem('sc.office.switches') ?? '{}') as Partial<Switches>;
    return { autopay: v.autopay !== false, receipts: v.receipts !== false, digest: v.digest === true };
  } catch {
    return { autopay: true, receipts: true, digest: false };
  }
}

export function HomeOffice() {
  const [tab, setTab] = useState<Tab>('runsheet');
  const [tasks, setTasks] = useState<Task[]>(() => [...SEED_TASKS, ...readExtra()]);
  const [done, setDone] = useState<string[]>(readDone);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newOwner, setNewOwner] = useState<string>(OWNERS[0]);
  const [filter, setFilter] = useState('');
  const [switches, setSwitches] = useState<Switches>(readSwitches);

  useEffect(() => {
    try {
      localStorage.setItem(DONE_KEY, JSON.stringify(done));
    } catch {
      /* non-fatal */
    }
  }, [done]);

  const persistSwitches = (next: Switches) => {
    setSwitches(next);
    try {
      localStorage.setItem('sc.office.switches', JSON.stringify(next));
    } catch {
      /* non-fatal */
    }
  };

  const toggle = (id: string) =>
    setDone((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const addTask = () => {
    const title = newTitle.trim();
    if (!title) return;
    const task: Task = { id: `t${Date.now()}`, title, owner: newOwner, due: 'NEW' };
    setTasks((prev) => [...prev, task]);
    try {
      localStorage.setItem(EXTRA_KEY, JSON.stringify([...readExtra(), task]));
    } catch {
      /* non-fatal */
    }
    setNewTitle('');
    setAdding(false);
  };

  const openCount = tasks.filter((t) => !done.includes(t.id)).length;
  const doneCount = done.filter((id) => tasks.some((t) => t.id === id)).length;
  const overdueCount = tasks.filter((t) => t.overdue && !done.includes(t.id)).length;

  const visibleSupplies = SUPPLIES.filter((s) =>
    (s.item + ' ' + s.sub).toLowerCase().includes(filter.toLowerCase()),
  );

  const payTotal = PAYABLES.reduce((a, p) => a + parseFloat(p.amount.replace('$', '')), 0).toFixed(2);

  return (
    <div className="dsp page">
      <header className="page__head">
        <SkinArt className="page__crest" src="/logos/dock-home-office.png" width={40} height={40} alt="" aria-hidden="true" />
        <div className="page__titles">
          <p className="eyebrow">home office · enzyme</p>
          <h1 className="h1">The office run</h1>
        </div>
        <div className="page__chips">
          <span className="badge badge-live"><span className="lamp" aria-hidden="true" />iMac node</span>
        </div>
      </header>

      <div className="tabs page__tabs" role="tablist" aria-label="Home office modules">
        {TABS.map((t) => (
          <button key={t.id} role="tab" type="button" aria-selected={tab === t.id} tabIndex={tab === t.id ? 0 : -1} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ RUNSHEET ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'runsheet'}>
        <div className="grid4">
          <div className="stat-tile"><span className="label">open</span><span className="val">{openCount}</span><span className="sub">On the runsheet.</span></div>
          <div className="stat-tile"><span className="label">done</span><span className="val">{doneCount}</span><span className="sub">Ticked off here.</span></div>
          <div className="stat-tile"><span className="label">overdue</span><span className="val">{overdueCount}</span><span className="sub">Past the promised day.</span></div>
          <div className="stat-tile"><span className="label">owner</span><span className="val" style={{ fontSize: 15 }}>2 roles</span><span className="sub">Operator and desk.</span></div>
        </div>

        <div className="row row--between">
          <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>runsheet</p>
          <span className="mono">{tasks.length} rows</span>
        </div>
        <ul className="rows" style={{ border: 'var(--ui-bw) solid var(--ui-border)', borderRadius: 'var(--ui-radius-ctl)', background: 'var(--ui-canvas)', overflow: 'auto' }}>
          {tasks.map((t) => (
            <li key={t.id} className={'lrow' + (done.includes(t.id) ? ' done' : '')}>
              <button className="toggle" type="button" aria-pressed={done.includes(t.id)} aria-label={`Mark done: ${t.title}`} onClick={() => toggle(t.id)} />
              <span className="lrow__t">{t.title}</span>
              <span className="mono">{t.owner}</span>
              <span className="mono">{t.due}</span>
            </li>
          ))}
        </ul>

        {!adding ? (
          <button className="btn btn--gold" type="button" onClick={() => setAdding(true)}>Add task</button>
        ) : (
          <div className="term stack">
            <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>new task</p><span className="tag tag--muted">this browser only</span></div>
            <div className="grid2">
              <div>
                <label className="lbl micro" htmlFor="atTitle">Task</label>
                <input className="field" id="atTitle" type="text" placeholder="what has to happen" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
              </div>
              <div>
                <label className="lbl micro" htmlFor="atOwner">Owner</label>
                <select className="field" id="atOwner" value={newOwner} onChange={(e) => setNewOwner(e.target.value)}>
                  {OWNERS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="row">
              <button className="btn btn--gold" type="button" onClick={addTask} disabled={newTitle.trim().length < 4}>Add to runsheet</button>
              <button className="btn btn--ghost" type="button" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        )}
      </section>

      {/* ══ PAYABLES ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'payables'}>
        <div className="grid3">
          <div className="stat-tile"><span className="label">monthly</span><span className="val">${payTotal}</span><span className="sub">Two subscriptions on record.</span></div>
          <div className="stat-tile"><span className="label">rail</span><span className="val" style={{ fontSize: 15 }}>AgentCard</span><span className="sub">Virtual card, issued per charge.</span></div>
          <div className="stat-tile"><span className="label">invoices to</span><span className="val" style={{ fontSize: 13 }}>quantas.molt</span><span className="sub">Agent mailbox on the bridge.</span></div>
        </div>
        <div className="row row--between">
          <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>recurring charges</p>
          <span className="tag tag--muted">as configured</span>
        </div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Vendor</th><th>Charge</th><th className="r">Amount</th><th>Cycle</th><th>State</th></tr></thead>
            <tbody>
              {PAYABLES.map((p, i) => (
                <tr key={i}><td>{p.vendor}</td><td>{p.charge}<span className="sub">{p.sub}</span></td><td className="r mono">{p.amount}</td><td>monthly</td><td><span className="badge badge-live"><span className="lamp" aria-hidden="true" />auto</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="notice notice--brass"><span className="tick" aria-hidden="true" /><span><strong>Human gate.</strong> Nothing is charged from this screen. A card has to exist on the rail first, and settlement is signed off by Mario.</span></p>
      </section>

      {/* ══ SUPPLIES ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'supplies'}>
        <div className="row row--between">
          <div className="grow"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>stock</p><p className="cap">Everything the storefront needs to ship a parcel.</p></div>
          <span className="mono">{visibleSupplies.length} rows</span>
        </div>
        <input className="field" type="search" placeholder="filter stock" aria-label="Filter stock" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Item</th><th className="r">On hand</th><th className="r">Reorder at</th><th>State</th></tr></thead>
            <tbody>
              {visibleSupplies.map((s, i) => (
                <tr key={i}>
                  <td>{s.item}{s.sub && <span className="sub">{s.sub}</span>}</td>
                  <td className="r mono">{s.onHand}</td>
                  <td className="r mono">{s.reorder}</td>
                  <td>{s.low ? <span className="tag tag--warn">reorder</span> : <span className="badge badge-live"><span className="lamp" aria-hidden="true" />fine</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row row--between"><span className="tag tag--muted">sample data</span><span className="cap">Counts are placeholders until the shelf is counted.</span></div>
      </section>

      {/* ══ SWITCHES ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'switches'}>
        <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>office switches</p>
        {([
          ['autopay', 'Auto-pay subscriptions', 'Issue a card when a Hostinger invoice lands, up to the monthly ceiling.'],
          ['receipts', 'Receipts to Mario', 'Forward every settlement receipt to the human gate.'],
          ['digest', 'Weekly office digest', 'One summary of runsheet, payables and stock, every Monday.'],
        ] as const).map(([key, title, note]) => (
          <div className="term row row--between" key={key}>
            <div className="grow"><p className="micro micro--ink">{title}</p><p className="cap">{note}</p></div>
            <button className="switch" type="button" aria-pressed={switches[key]} aria-label={title} onClick={() => persistSwitches({ ...switches, [key]: !switches[key] })}>
              <span className="switch__track"><span className="switch__knob" /></span>
            </button>
          </div>
        ))}
        <p className="cap">Switches are held in this browser. Nothing here reaches the rail on its own.</p>
      </section>

      <p className="page__foot">Enzyme is the office run: what has to happen this week, what is already paid, and what is running low.</p>
    </div>
  );
}
